// Thin wrappers around fetch.
// Em dev (docker-compose / vite dev server), VITE_BACKEND_URL aponta para http://localhost:8080.
// Em produção (Azure Container Apps), VITE_BACKEND_URL é injetado no build pelo deploy.sh.
 
import type { AnonymizationMode, ScanResponse } from './types/api';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL ?? '';

export async function scanFile(file: File, tenantId: string): Promise<ScanResponse> {
  const form = new FormData();
  form.append('file', file);

  const res = await fetch(`${BACKEND_URL}/scan`, {
    method: 'POST',
    headers: { 'X-Tenant-Id': tenantId },
    body: form,
  });
  
  if (!res.ok) {
    throw new Error(`Scan failed (HTTP ${res.status}): ${await res.text()}`);
  }
  return res.json();
}

/** /pseudonymize found other detections than the scan did (HTTP 409). */
export class ScanOutdatedError extends Error {}

export interface PseudonymizeResult {
  blob: Blob;
  filename: string;
  findings: number;
  mode: AnonymizationMode;
}

export async function pseudonymizeFile(
  file: File,
  tenantId: string,
  detectionIndices: number[],
  mode: AnonymizationMode,
  maskChar: string,
  detectionsHash: string,
): Promise<PseudonymizeResult> {
  const form = new FormData();
  form.append('file', file);
  form.append('detection_indices', detectionIndices.join(','));
  form.append('mode', mode);
  form.append('mask_char', maskChar);
  form.append('detections_hash', detectionsHash);

  const res = await fetch(`${BACKEND_URL}/pseudonymize`, {
    method: 'POST',
    headers: { 'X-Tenant-Id': tenantId },
    body: form,
  });

  if (res.status === 409) {
    throw new ScanOutdatedError('O documento mudou desde a análise. Analise o arquivo novamente.');
  }
  if (!res.ok) {
    throw new Error(`Anonymize failed (HTTP ${res.status}): ${await res.text()}`);
  }

  const blob = await res.blob();
  const cd = res.headers.get('Content-Disposition') ?? '';
  const match = cd.match(/filename="([^"]+)"/);
  const filename = match ? match[1] : `anonymized__${file.name}`;
  const findings = parseInt(res.headers.get('X-PII-Findings') ?? '0', 10);
  const appliedMode =
    (res.headers.get('X-Anonymization-Mode') as AnonymizationMode | null) ?? mode;

  return { blob, filename, findings, mode: appliedMode };
}

export function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
